import { useEffect, useRef } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import apiClient from "../api/client";

const AxiosInterceptor = () => {
  const { getAccessTokenSilently, isAuthenticated, loginWithRedirect, isLoading } = useAuth0();

  const requestInterceptorId = useRef(null);
  const responseInterceptorId = useRef(null);

  useEffect(() => {
    if (requestInterceptorId.current !== null) {
      apiClient.interceptors.request.eject(requestInterceptorId.current);
    }
    if (responseInterceptorId.current !== null) {
      apiClient.interceptors.response.eject(responseInterceptorId.current);
    }

    if (isLoading && !isAuthenticated) {
      requestInterceptorId.current = apiClient.interceptors.request.use(
        (config) => {
          if (config.url?.startsWith("/api")) {
            return Promise.reject(new Error("Authentication status pending"));
          }
          return config;
        },
        (error) => Promise.reject(error)
      );
      return;
    }

    if (isAuthenticated && getAccessTokenSilently) {
      requestInterceptorId.current = apiClient.interceptors.request.use(
        async (config) => {
          if (config.url?.startsWith("/api")) {
            try {
              const token = await getAccessTokenSilently();
              config.headers.Authorization = `Bearer ${token}`;
            } catch (_error) {
              loginWithRedirect({
                appState: {
                  returnTo: window.location.pathname + window.location.search,
                },
              });
              return Promise.reject(
                new Error("Redirecting to login due to authentication failure")
              );
            }
          }
          return config;
        },
        (error) => {
          return Promise.reject(error);
        }
      );

      responseInterceptorId.current = apiClient.interceptors.response.use(
        (response) => response,
        async (error) => {
          if (error.config?.url?.startsWith("/api") && [403, 401].includes(error.response?.status)) {
            loginWithRedirect({
              appState: {
                returnTo: window.location.pathname + window.location.search,
              },
            });
            return Promise.reject(
              new Error("Redirecting to login due to authentication failure")
            );
          } else {
            return Promise.reject(error);
          }
        }
      );
    }

    return () => {
      if (requestInterceptorId.current !== null) {
        apiClient.interceptors.request.eject(requestInterceptorId.current);
        requestInterceptorId.current = null;
      }
      if (responseInterceptorId.current !== null) {
        apiClient.interceptors.response.eject(responseInterceptorId.current);
        responseInterceptorId.current = null;
      }
    };
  }, [getAccessTokenSilently, isAuthenticated, loginWithRedirect, isLoading]);

  return null;
};

export default AxiosInterceptor;
